// Sugalarm — native push lifecycle (cycle 28).
//
// Owns the post-permission registration handshake. On a non-Capacitor
// surface (kiosk Chromium, browser, installed PWA) every export here
// short-circuits — the cycle-17 Web Push path is the right rail for
// those surfaces and stays untouched.
//
// `useNativePush` (web/src/hooks/useNativePush.ts) is the React-facing
// hook; this module is the imperative core both the hook and the
// App.tsx bootstrap effect call into.

import * as nativePushApi from "../api/nativePush";
import { getDeviceId } from "./auth";
import {
  clearPushTokenRegistered,
  markPushTokenRegistered,
} from "./activation";
import {
  isCapacitorNative,
  registerForPushNotifications,
  requestPushPermission,
  type NativePushPermission,
} from "./capacitor";
// Cycle 307: the KioskOta bridge doubles as the "official kiosk APK,
// vc18+, FCM-safe" detector for the auto-arm below.
import * as kioskOta from "./kioskOta";

const APNS_BUNDLE_ID_KEY = "sugalarm.nativePush.bundleId";

function readBundleId(): string {
  try {
    const v = localStorage.getItem(APNS_BUNDLE_ID_KEY);
    if (v && v.length > 0) return v;
  } catch {
    /* ignore */
  }
  // Hard-coded fallback matches `mobile/capacitor.config.ts`'s `appId`.
  // If the operator renames the bundle, they can override here via
  // localStorage before launch — same posture as the cycle-26 kiosk
  // flag.
  return "dev.sugalarm.app";
}

function writeBundleId(v: string) {
  try {
    localStorage.setItem(APNS_BUNDLE_ID_KEY, v);
  } catch {
    /* ignore */
  }
}

/** Best-effort lookup of the Capacitor app bundle id at runtime.
 *  Falls back to the cached / default value on any error. */
async function resolveBundleId(): Promise<string> {
  if (!isCapacitorNative()) return readBundleId();
  try {
    // Bridge-injected plugin proxy — a bare `import("@capacitor/app")`
    // can't resolve in the pure-remote webview.
    const appProxy = (
      window as {
        Capacitor?: {
          Plugins?: { App?: { getInfo?: () => Promise<{ id?: string }> } };
        };
      }
    ).Capacitor?.Plugins?.App;
    const info = await appProxy?.getInfo?.();
    if (info?.id) {
      writeBundleId(info.id);
      return info.id;
    }
  } catch {
    /* ignore */
  }
  return readBundleId();
}

/** Has the operator already wired native push for this auth session?
 *  Stored in localStorage so we don't spam the permission prompt on
 *  every page refresh. */
const ENABLED_FLAG_KEY = "sugalarm.nativePush.enabled";

export function isNativePushEnabledLocally(): boolean {
  try {
    return localStorage.getItem(ENABLED_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

export function setNativePushEnabledLocally(on: boolean) {
  try {
    if (on) localStorage.setItem(ENABLED_FLAG_KEY, "1");
    else localStorage.removeItem(ENABLED_FLAG_KEY);
  } catch {
    /* ignore */
  }
}

export interface BootstrapOptions {
  /** Called when the user taps a notification banner or the OS
   *  delivers a `sugalarm://alerts?id=…` URL. */
  onAlertId: (alertId: string) => void;
}

/** Bootstrap native push: ask for permission (if not already
 *  resolved), then wire the listeners + register with APNS / FCM.
 *  Resolves to a teardown function. No-ops to a no-op teardown on
 *  non-Capacitor surfaces.
 *
 *  Safe to call multiple times — the underlying listeners are
 *  idempotent at the OS layer, but multiple calls here will create
 *  duplicate listener handles. Callers (App.tsx) should call once
 *  per signed-in session and hold the teardown function. */
export async function bootstrapNativePush(
  opts: BootstrapOptions,
): Promise<() => void> {
  if (!isCapacitorNative()) return () => undefined;
  // Cycle 204 / 213: native push works on iOS (APNS) and BOTH Android flavors
  // (FCM). The kiosk APK (cycle 204) and the standard phone APK (cycle 213)
  // each ship google-services.json for the `sugalarm-push` Firebase project,
  // so FirebaseApp auto-initializes and register() no longer throws the
  // 0.198.1 "Default FirebaseApp is not initialized" crash. The cycle-203.1
  // iOS-only guard is lifted. (Crash-safe sequencing: an Android build WITHOUT
  // FCM would still crash on register(), so the FCM-configured APK is always
  // distributed BEFORE any phone arms push — a token only exists after that.)
  // Don't pop the permission prompt unless the user has opted in via
  // Settings → Notifications (or a prior session left the flag set).
  //
  // Cycle 307 exception — the official Android kiosk shell AUTO-ARMS:
  // an unattended tablet kiosk has no user to tap "Enable on this
  // device", and the cycle-203.1 crash fix cleared this flag on Android
  // fleet-wide without anything ever re-arming it — which left the whole
  // kiosk fleet without FCM tokens (no alarm push, no OTA nudge reach).
  // Gate = the KioskOta bridge (vc18+ APK): every KioskOta-bearing build
  // ships google-services.json (FCM landed at vc9), so the 203.1 crash
  // class — register() on an FCM-less build — is impossible here. A
  // pre-vc18 shell (no KioskOta) NEVER auto-arms. Deliberate: the kiosk
  // is an alarm appliance, so push stays armed even if someone toggled
  // it off in Settings (documented in the cycle-307 CHANGELOG entry).
  // On Android 13+ registration still needs POST_NOTIFICATIONS — the
  // vc29 device-owner auto-grant (this same cycle) makes that a no-op;
  // an ungranted unit simply no-ops here and retries next launch.
  if (!isNativePushEnabledLocally() && kioskOta.isAvailable()) {
    setNativePushEnabledLocally(true);
  }
  if (!isNativePushEnabledLocally()) return () => undefined;

  const permission: NativePushPermission = await requestPushPermission();
  if (permission !== "granted") {
    // User declined or system can't grant. Leave the local flag set so
    // a re-prompt isn't required next launch — but no listeners get
    // wired.
    return () => undefined;
  }

  const bundleId = await resolveBundleId();

  return registerForPushNotifications({
    onToken: async (platform, token) => {
      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent ?? null : null;
      // ⚠ Cycle 418: remember what we registered so sign-out can hand it
      // back. The token arrives only in this callback and was previously
      // dropped, which left the SPA unable to name its own push credential at
      // teardown — so a signed-out handset kept a live registration until some
      // other account happened to re-register the same token.
      rememberRegisteredPushToken(token);
      const r = await nativePushApi.registerToken({
        platform,
        token,
        bundle_id: bundleId,
        device_id: getDeviceId() ?? null,
        user_agent: userAgent,
      });
      if (!r.ok) {
        console.warn(
          "[nativePush] register failed:",
          r.error.status,
          r.error.message,
        );
        // Cycle 365: a registration that fails here is exactly how a phone
        // ends up holding a glance surface with no way to be paged. Clear
        // the claim so the activation report says "not registered" instead
        // of leaving a stale yes behind.
        clearPushTokenRegistered();
      } else {
        markPushTokenRegistered();
      }
    },
    onAlertId: opts.onAlertId,
  });
}

/** Force-enable from Settings: persist the local flag, request
 *  permission immediately, register, return the permission status so
 *  the UI can render the right pill / hint. */
export async function enableNativePush(
  opts: BootstrapOptions,
): Promise<NativePushPermission> {
  // Cycle 204 / 213: native push works on iOS (APNS) + Android (FCM — kiosk
  // APK cycle 204, standard phone APK cycle 213). The cycle-203.1 Android
  // refusal is lifted now that both Android APKs are FCM-configured.
  setNativePushEnabledLocally(true);
  const permission = await requestPushPermission();
  if (permission === "granted") {
    await bootstrapNativePush(opts);
  }
  return permission;
}

/** Force-disable from Settings: clear local flag, remove every token
 *  this user has on this device. The OS-level permission is left
 *  intact (we can't revoke it from JS). */
export async function disableNativePush(): Promise<void> {
  setNativePushEnabledLocally(false);
  // Best-effort cleanup: delete every token row whose `device_id`
  // matches this client's device_id, since a re-install on the same
  // device would re-issue the APNS / FCM token anyway and re-register
  // a fresh row.
  const list = await nativePushApi.listTokens();
  if (!list.ok) return;
  const deviceId = getDeviceId();
  if (!deviceId) return;
  for (const t of list.data.tokens) {
    if (t.device_id === deviceId) {
      await nativePushApi.deleteToken(t.id);
    }
  }
}

// ═════════════════════════════════════════════════════════════════════════
// Cycle 418 — the last push token this install registered.
// ═════════════════════════════════════════════════════════════════════════
//
// ⚠ This is a push ADDRESS, not a secret: it grants nothing on its own and
// the server already rejects a registration that does not carry a valid JWT.
// It is stored so `teardownAccount` can name it in the release body; it is
// cleared the moment it is taken.

const PUSH_TOKEN_KEY = "sugalarm.lastPushToken";

/**
 * Cycle 470 — every push token this install has registered, not only the
 * last one. APNs rotates a phone's token (reinstall, restore, OS update) and
 * accepts-and-drops a superseded one for a while (cycle 421.1 measured APNs
 * taking a token the phone had replaced eight minutes earlier), so a sign-out
 * that names only the CURRENT token leaves the superseded rows live under
 * the old account. Deduplicated and bounded to the last 8, mirroring
 * `liveActivity.rememberLiveActivityToken`. Same posture as the key above: a
 * push ADDRESS, not a secret.
 */
const PUSH_TOKEN_HISTORY_KEY = "sugalarm.pushTokenHistory";
const PUSH_TOKEN_HISTORY_MAX = 8;

export function rememberRegisteredPushToken(token: string): void {
  if (!token) return;
  // The token this call REPLACES is a superseded token from now on — and a
  // device arriving from a pre-470 bundle holds exactly one of those with
  // no history yet (review round 1). Read it before overwriting, so it is
  // seeded into the history below rather than lost.
  let previous: string | null = null;
  try {
    previous = localStorage.getItem(PUSH_TOKEN_KEY) || null;
  } catch {
    previous = null;
  }
  try {
    localStorage.setItem(PUSH_TOKEN_KEY, token);
  } catch {
    /* storage unavailable */
  }
  // Separate try: a failure to append history must never cost the current
  // token above, and vice versa. Oldest first, each token once, the newest
  // PUSH_TOKEN_HISTORY_MAX kept.
  try {
    const next: string[] = [];
    for (const t of [...rememberedPushTokenHistory(), previous, token]) {
      if (t && !next.includes(t)) next.push(t);
    }
    localStorage.setItem(
      PUSH_TOKEN_HISTORY_KEY,
      JSON.stringify(next.slice(-PUSH_TOKEN_HISTORY_MAX)),
    );
  } catch {
    /* storage unavailable */
  }
}

/** Cycle 470 — the remembered history, oldest first; `[]` when none or when
 *  storage is unavailable or holds something that is not a string list.
 *  Capped on READ as well as on write (security review): an oversized or
 *  tampered store must never push the release body past the server's
 *  32-item cap and 422 the whole release, tile and watch included. */
export function rememberedPushTokenHistory(): string[] {
  try {
    const raw = localStorage.getItem(PUSH_TOKEN_HISTORY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    const list = Array.isArray(parsed)
      ? parsed.filter((t): t is string => typeof t === "string" && !!t)
      : [];
    return list.slice(-PUSH_TOKEN_HISTORY_MAX);
  } catch {
    return [];
  }
}

/**
 * Read WITHOUT clearing.
 *
 * ⚠ THIS USED TO READ-AND-CLEAR, and that lost credentials. Sign-out collects
 * the token, then hands it back over a network that may not be there — and on
 * the auth-REJECTION path the JWT is already dead, so the release is refused.
 * A destructive read threw the token away in exactly those cases, leaving the
 * server row live with nothing left on the device able to name it. Peek here;
 * `forgetRegisteredPushToken()` is called only once the release SUCCEEDS.
 */
export function peekRegisteredPushToken(): string | null {
  try {
    return localStorage.getItem(PUSH_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

/** Clears BOTH the current token and the cycle-470 history: called only once
 *  the server has accepted the release (see `teardownAccount`). */
export function forgetRegisteredPushToken(): void {
  try {
    localStorage.removeItem(PUSH_TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    localStorage.removeItem(PUSH_TOKEN_HISTORY_KEY);
  } catch {
    /* storage unavailable */
  }
}
